/**
 * Download a file from an authenticated API endpoint.
 * Fetches with the Bearer token, validates the response is not an error,
 * and triggers a browser download via a temporary blob URL.
 */

/** App tool results use `/api/file/:fileId` — requires Bearer or ?token=. */
const PROTECTED_FILE_PATH = /\/api\/file\/[^/?#]+/i;

export function isProtectedFileDownloadUrl(url: string): boolean {
  if (!url || url.startsWith('blob:') || url.startsWith('data:')) return false;
  return PROTECTED_FILE_PATH.test(url);
}

/**
 * Download via authenticated fetch when the URL is a protected `/api/file/...` route;
 * otherwise use a plain anchor (external or public URLs).
 */
export async function downloadUrlWithAuthIfNeeded(
  url: string,
  fileName: string,
  getToken: () => string | null,
): Promise<void> {
  if (!url) throw new Error('Missing download URL');
  if (url.startsWith('blob:') || url.startsWith('data:')) {
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    return;
  }
  if (isProtectedFileDownloadUrl(url)) {
    await authedDownload(url, fileName, getToken);
    return;
  }
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

export async function authedDownload(
  url: string,
  fileName: string,
  getToken: () => string | null,
): Promise<void> {
  const token = getToken();
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const response = await fetch(url, { headers });

  if (!response.ok) {
    // Don't save error responses as files
    const contentType = response.headers.get('content-type') ?? '';
    if (contentType.includes('json')) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.message ?? data.error ?? `Download failed (${response.status})`);
    }
    throw new Error(`Download failed (${response.status})`);
  }

  const blob = await response.blob();
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(blobUrl);
}

/**
 * Append auth token as query param to a URL (for iframe src, img src, etc.)
 */
export function appendTokenToUrl(url: string, getToken: () => string | null): string {
  const token = getToken();
  if (!token) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}token=${encodeURIComponent(token)}`;
}

/** Fetch a document URL (including protected `/api/file/…`) into a File for uploads (e.g. e-sign execute). */
export async function fetchUrlAsFile(
  url: string,
  fileName: string,
  mimeType: string,
  getToken: () => string | null,
): Promise<File> {
  if (!url) throw new Error('Missing URL');
  const fetchUrl =
    isProtectedFileDownloadUrl(url) ? appendTokenToUrl(url, getToken) : url;
  const res = await fetch(fetchUrl);
  if (!res.ok) {
    throw new Error(`Could not read document (${res.status})`);
  }
  const blob = await res.blob();
  return new File([blob], fileName, { type: mimeType || blob.type || 'application/pdf' });
}
